import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Context } from '../../../Context';
import CustomToolTip from '../../ToolTipInfoCustomMUI';
import Zoom from '@mui/material/Zoom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import { ReactComponent as IconDownload } from '../../../media/img/icon-download.svg';
import { ReactComponent as IconChevron } from '../../../media/img/icon-chevron.svg';
import PdfModal from './PdfModal';

import bim_styles from '../../../styles/bim.module.scss';
import apart_styles from '../../../styles/aprt.module.scss';
import buttons_styles from '../../../styles/buttons.module.scss';

function AdditionalResources() {
	const { dataUrl, projectSetup, projectStyle, activeApartmentData, isMobile } =
		useContext(Context);

	const { t } = useTranslation();

	const [showResourcesList, setShowResourcesList] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pdfUrl, setPdfUrl] = useState('');
	const [snackbarOpen, setSnackbarOpen] = useState(false);

	const additionalResources = projectSetup?.additional_resources; //TODO: implement this data
	// const additionalResources = [
	// 	{
	// 		name: 'idUnitDetailsSheet',
	// 		name_original: 'Karta lokalu PDF',
	// 	},
	// 	{
	// 		name: 'idInformationProspectus',
	// 		name_original: 'Prospekt informacyjny',
	// 		path: '',
	// 	},
	// 	// {
	// 	//     name: "idSitePlan",
	// 	//     name_original: "Plan Osiedla PDF",
	// 	//     path: "",
	// 	// },
	// 	// {
	// 	//     name: "idOfferTermsAndConditions",
	// 	//     name_original: "Regulamin ofert specjalnych",
	// 	//     path: "",
	// 	// },
	// ];

	const getTooltipText = (resourceName) => {
		switch (resourceName) {
			case 'idUnitDetailsSheet':
				return t('download_unit_sheet');
			case 'idInformationProspectus':
				return t('download_prospectus');
			case 'idSitePlan':
				return t('download_site_plan');
			case 'idOfferTermsAndConditions':
				return t('download_offer_terms');
			default:
				return '';
		}
	};

	const handleClickOnResourceDownload = async (resourceName, currentUuid) => {
		switch (resourceName) {
			case 'idUnitDetailsSheet':
				return await handleShowAndDownloadUnitSheet(currentUuid);
			default:
				return await handleDownloadResource(resourceName);
		}
	};

	const unitSheetsConfig = projectSetup?.pdf_sheets_config || {};
	const currentUuid = activeApartmentData?.uuid || '';

	const handleSnackbarClose = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}

		setSnackbarOpen(false);
	};

	const handleDownloadResource = async (resourceName) => {
		const resourceData = additionalResources.find(
			(resource) => resource.name === resourceName
		);
		console.log('Resource data found:', resourceData);

		if (!resourceData) {
			console.error('Resource not found:', resourceName);
			setSnackbarOpen(true);
			return;
		}

		const path = resourceData?.path ?? '';
		if (!path) {
			console.error('Resource path not found for:', resourceName, resourceData);
			setSnackbarOpen(true);
			return;
		}

		let objectUrl;
		try {
			const response = await fetch(path);
			if (!response.ok) {
				throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
			}

			const blob = await response.blob();
			objectUrl = URL.createObjectURL(blob);

			const rawName = resourceData.name_original || `${projectSetup?.developer || ''}_${projectSetup?.name || ''}_document`;
			const safeName = String(rawName).trim().replace(/\s+/g, '_');
			const downloadName = safeName.toLowerCase().endsWith('.pdf') ? safeName : `${safeName}.pdf`;

			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = downloadName;
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			console.error('Failed to download resource:', err);
			setSnackbarOpen(true);
		} finally {
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl);
			}
		}
	};

	const handleShowAndDownloadUnitSheet = async (uuid) => {
		const fileNameSourceKey = unitSheetsConfig.source_key || 'id_original';
		const fileNameSource = activeApartmentData[fileNameSourceKey];
		const sourceDivider = unitSheetsConfig.source_divider || '/';
		const targetDivider = unitSheetsConfig.target_divider || '-';
		const fileNameUnitPart = fileNameSource
			.split(sourceDivider)
			.join(targetDivider);

		const developerName = projectSetup?.developer?.split(' ').join('-') || '';
		const project = projectSetup?.name?.split(' ').join('-') || '';
		const fileNamePrefix = unitSheetsConfig.file_name_prefix;
		const fileNameSuffix = unitSheetsConfig.file_name_suffix;

		const fileName =
			(fileNamePrefix ? `${fileNamePrefix}_` : `${developerName}_${project}_`) +
			`${fileNameUnitPart}` +
			(fileNameSuffix ? `_${fileNameSuffix}` : '');

		const basePath = `${dataUrl}apartments/${uuid}`;
		const fileExt = unitSheetsConfig.file_extension || '.pdf';
		const url = `${basePath}/${uuid}${fileExt}`;
		let downloadSucceeded = false;
		try {
			const response = await fetch(url);
			if (!response.ok)
				throw new Error(`Network response was not ok: ${response.status}`);

			// Read as ArrayBuffer so we can inspect bytes (to detect HTML masquerading as PDF)
			const arrayBuffer = await response.arrayBuffer();
			if (!arrayBuffer || arrayBuffer.byteLength === 0)
				throw new Error('Fetched file is empty');

			const contentType = (
				response.headers.get('content-type') || ''
			).toLowerCase();
			const uint8 = new Uint8Array(arrayBuffer);
			const header = String.fromCharCode(...uint8.subarray(0, 4)); // first 4 bytes

			// PDF files start with "%PDF"
			const looksLikePdf = contentType.includes('pdf') || header === '%PDF';

			if (!looksLikePdf) {
				// optional: decode a small text snippet for debugging (HTML returns readable text)
				const snippet = new TextDecoder().decode(uint8.subarray(0, 200));
				throw new Error(
					`Fetched file is not a PDF. Content-Type: ${contentType}. Snippet: ${snippet}`
				);
			}

			// create a blob and continue
			const blob = new Blob([arrayBuffer], { type: 'application/pdf' });

			const objectUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = `${fileName}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(objectUrl);

			downloadSucceeded = true;
		} catch (err) {
			console.error('Failed to download PDF:', err);
			setSnackbarOpen(true);
			// don't proceed to open modal or set pdfUrl when download failed
		}

		if (!downloadSucceeded) return;

		setPdfUrl(url);
		setIsModalOpen(true);
	};

	if (!additionalResources || additionalResources.length === 0) {
		return null;
	}
	return (
		<>
			{additionalResources.length === 1 && (
				<div // ----------------------------- additional resource container -----------------------------
					style={{
						width: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '2.5rem',
						wrap: 'nowrap',
						marginTop: '1rem',
						paddingLeft: '0.5rem',
					}}
				>
					<span //h4?
						style={{
							display: 'inline-block',
							fontSize: ' 1.2rem',
							fontWeight: '600',
							color: 'var(--color-headings)',
						}}
					>
						{getTooltipText(additionalResources[0].name)}
					</span>
					<CustomToolTip
						title={getTooltipText(additionalResources[0].name)}
						slots={{
							transition: Zoom,
						}}
						arrow
					>
						<button
							className={buttons_styles['bim_button--no_border']}
							onClick={() =>
								handleClickOnResourceDownload(
									additionalResources[0].name,
									currentUuid
								)
							}
						>
							<IconDownload
								title={getTooltipText(additionalResources[0].name)}
								className={buttons_styles['bim_button__icon']}
							/>
						</button>
					</CustomToolTip>
				</div>
			)}
			{additionalResources.length > 1 && (
				<div
					// -----------------------------  additional resources box  -----------------------------
					style={{
						width: '100%',
						display: 'flex',
						flexDirection: 'column',
						transition: 'max-height 800ms ease',
						overflow: 'hidden',
						marginTop: '1rem',

						maxHeight: showResourcesList ? '600px' : '56px', // 56px ≈ wysokość tylko nagłówka; dopasuj 600px do maksymalnej wysokości listy}
					}}
				>
					<div // ----------------------------- additional resource header -----------------------------
						style={{
							width: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: '2.5rem',
							wrap: 'nowrap',
							paddingLeft: '0.5rem',
						}}
					>
						<span //h4?
							style={{
								display: 'inline-block',
								fontSize: ' 1.2rem',
								fontWeight: '600',
								color: 'var(--color-headings)',
							}}
						>
							{t('additional_resources')}
						</span>

						<CustomToolTip
							title={'Pokaż materiały dodatkowe do pobrania'}
							slots={{
								transition: Zoom,
							}}
							arrow
						>
							<button
								className={buttons_styles['bim_button--no_border']}
								onClick={() => setShowResourcesList(!showResourcesList)}
							>
								<IconChevron
									title={getTooltipText(additionalResources[0].name)}
									className={buttons_styles['bim_button__icon']}
									style={{
										transform: !showResourcesList
											? 'rotate(90deg)'
											: 'rotate(-90deg)',
										transition: 'transform 300ms ease-in-out',
									}}
								/>
							</button>
						</CustomToolTip>
					</div>
					{showResourcesList &&
						additionalResources.map((resource, index) => (
							<div
								key={index}
								style={{
									width: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '2.5rem',
									wrap: 'nowrap',
									paddingLeft: '0.5rem',
								}}
							>
								<h4>{resource.name_original}</h4>
								<CustomToolTip
									title={getTooltipText(resource.name)}
									slots={{
										transition: Zoom,
									}}
									arrow
								>
									<button
										className={buttons_styles['bim_button--no_border']}
										onClick={() =>
											handleClickOnResourceDownload(resource.name, currentUuid)
										}
									>
										<IconDownload
											title={getTooltipText(resource.name)}
											className={buttons_styles['bim_button__icon']}
										/>
									</button>
								</CustomToolTip>
							</div>
						))}
				</div>
			)}
			{isModalOpen && (
				<PdfModal
					isOpen={isModalOpen}
					pdfUrl={pdfUrl}
					onClose={() => setIsModalOpen(false)}
				/>
			)}
			<Snackbar
				open={snackbarOpen}
				autoHideDuration={3000}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				onClose={handleSnackbarClose}
			>
				<Alert onClose={handleSnackbarClose} severity="error" variant="filled">
					{t('pdf_download_error')}
				</Alert>
			</Snackbar>
		</>
	);
}

export default AdditionalResources;
